import React, { Component, ErrorInfo, ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

/**
 * Componente ErrorBoundary para capturar errores en componentes hijos
 * y mostrar una UI alternativa en lugar de fallar la aplicación completa.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  /**
   * Actualiza el estado cuando ocurre un error
   */
  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  /**
   * Captura y registra información detallada del error
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Registra el error en la consola
    console.error('Error capturado por ErrorBoundary:', error);
    console.error('Información del error:', errorInfo);
    
    // Actualiza el estado con la información del error
    this.setState({
      errorInfo
    });
    
    // Aquí podrías enviar el error a un servicio de monitoreo
    // logErrorToService(error, errorInfo);
  }

  /**
   * Intenta recuperarse del error
   */
  tryAgain = (): void => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render(): ReactNode {
    // Si hay un error, muestra el contenido alternativo (fallback)
    if (this.state.hasError) {
      // Si el fallback incluye un botón para reintentar, podemos pasarle el método tryAgain
      // Para usar esto, modifica tu fallback para aceptar una prop onRetry y úsala en un botón
      const fallbackWithRetry = React.isValidElement(this.props.fallback) 
        ? React.cloneElement(this.props.fallback, { 
            onRetry: this.tryAgain,
            error: this.state.error,
            errorInfo: this.state.errorInfo
          })
        : this.props.fallback;
      
      return fallbackWithRetry;
    }

    // Si no hay error, renderiza los componentes hijos normalmente
    return this.props.children;
  }
}